import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, PRIMARY_OUTLET, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';

/**
 * Builds a PrimeNG `MenuItem[]` breadcrumb trail from the activated route tree.
 *
 * Each route segment that carries `data.breadcrumb` becomes one crumb.  The
 * crumb's `routerLink` is the accumulated URL up to and including that segment,
 * so every crumb is individually clickable.
 *
 * Usage in a layout component:
 * ```ts
 * breadcrumbSvc = inject(BreadcrumbService);
 * breadcrumbs$ = this.breadcrumbSvc.breadcrumbs$;
 * ```
 *
 * Labels that are only known once data has loaded — an exam phase, a draw
 * name, an application number — cannot live in static route data, and some
 * trails are deeper than the route tree (application-review's draw routes are
 * flat siblings, not nested).  Those pages call `setTrail()` instead of
 * rendering a second breadcrumb of their own.
 *
 * Labels are run through ngx-translate, which returns a key unchanged when it
 * has no entry for it. Management routes can therefore keep their plain
 * English labels while the applicant-facing routes pass translation keys, and
 * the trail re-renders itself when the user switches EN/FR.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly _crumbs$ = new BehaviorSubject<MenuItem[]>([]);

  /** Observable trail of crumbs for the current route. */
  readonly breadcrumbs$ = this._crumbs$.asObservable();

  private readonly translate = inject(TranslateService);

  /** Crumbs the active page supplied for its own route, if any. */
  private trail: MenuItem[] | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {
    // Only update after navigation is fully settled — the snapshot tree is
    // not populated during the auth/callback redirect, so we must NOT seed
    // eagerly from the constructor.
    // Reset on NavigationStart, emit on NavigationEnd. The order matters: a
    // component's ngOnInit runs while the route is activating, i.e. before
    // NavigationEnd, so clearing on NavigationEnd would throw away a trail
    // that was just set. Clearing on NavigationStart drops the outgoing
    // page's trail instead, which is what we actually want.
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.trail = null;
      } else if (e instanceof NavigationEnd) {
        this.emit();
      }
    });

    this.translate.onLangChange.subscribe(() => this.emit());
  }

  /**
   * Replace the crumb contributed by the caller's own route with a richer
   * trail. Ancestors still come from route data, so the result is one
   * continuous line rather than a second breadcrumb. Cleared on navigation,
   * so callers re-set it whenever their data resolves.
   */
  setTrail(crumbs: MenuItem[]): void {
    this.trail = crumbs;
    this.emit();
  }

  private emit(): void {
    const crumbs = this.build(this.route.root);
    if (this.trail) {
      crumbs.splice(-1, 1, ...this.trail);
    }
    this._crumbs$.next(crumbs.map((c) => (c.label ? { ...c, label: this.translate.instant(c.label) } : c)));
  }

  /**
   * Recursively walks the primary outlet route tree, collecting segments whose
   * snapshot carries a non-empty `data.breadcrumb` string.
   */
  private build(route: ActivatedRoute, url = '', crumbs: MenuItem[] = []): MenuItem[] {
    for (const child of route.children) {
      if (child.outlet !== PRIMARY_OUTLET) continue;

      // Guard: snapshot may be absent during early navigation phases.
      if (!child.snapshot) continue;

      // Accumulate the URL path for this segment.
      const segments = child.snapshot.url.map((s) => s.path);
      if (segments.length) {
        url += '/' + segments.join('/');
      }

      const label: string | undefined = child.snapshot.data['breadcrumb'];
      if (label) {
        crumbs.push({ label, routerLink: url });
      }

      // Recurse into children — we always follow the primary outlet.
      return this.build(child, url, crumbs);
    }

    return crumbs;
  }
}
